import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useController } from '@rest-hooks/react';

import ProductInstanceResource from 'resources/organization/ProductInstanceResource';
import ShipmentResource from 'resources/organization/ShipmentResource';

import Button from 'ui/Button';
import Subtitle from 'ui/text/Subtitle';
import StyledDialog from 'ui/StyledComponents/StyledDialog';
import CustomChip from 'ui/CustomChip';

import { Checkbox, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import EmailIcon from '@mui/icons-material/Email';
import EventIcon from '@mui/icons-material/Event';

import formattedDate from 'utils/formattedDate';
import { useError } from 'utils/useErrorController';

import ShipmentForm from './ShipmentForm';
import ShipmentCard from './ShipmentCard';

const formatAddress = (shippingAddress) => {
  if (!shippingAddress) return '';

  const { name, address, post_code, city, country } = shippingAddress;
  return [name, address, post_code, city, country].filter(Boolean).join(', ');
};

// SELL21 — the shipping workbench: one order in isolation, its pending items
// as a checklist feeding the Ship form, and one card per parcel already sent.
const OrderDetailsModal = ({ row, organizationId, onChanged, onClose }) => {
  const { fetch } = useController();
  const { handleError } = useError();
  const [items, setItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [shipFormOpen, setShipFormOpen] = useState(false);

  // The table page may hide siblings (status filter, pagination), so the
  // modal fetches the order's fulfillable items fresh. A numeric query is an
  // exact order_id match API-side. Every queue row has an order (SELL21).
  const loadItems = async () => {
    if (!row) return;
    const records = await fetch(ProductInstanceResource.list(), {
      organization_id: organizationId,
      fulfillment: true,
      query: `${row.order_id}`,
    });
    setItems(records);
    setSelectedIds(records.filter((item) => !item.shipment).map((item) => item.id));
  };

  useEffect(() => {
    loadItems();
  }, [row?.id]);

  if (!row) return null;

  const pendingItems = items.filter((item) => !item.shipment);

  // Numbered by creation order so "Shipment #1" is the first parcel sent.
  const shipments = Object.values(
    items.reduce((groups, item) => {
      if (!item.shipment) return groups;
      if (!groups[item.shipment.id]) {
        groups[item.shipment.id] = { ...item.shipment, items: [] };
      }
      groups[item.shipment.id].items.push(item);
      return groups;
    }, {})
  ).sort((a, b) => a.id - b.id);

  const refresh = async () => {
    await loadItems();
    onChanged();
  };

  const toggleSelected = (id) => {
    setSelectedIds(
      selectedIds.includes(id)
        ? selectedIds.filter((selectedId) => selectedId !== id)
        : [...selectedIds, id]
    );
  };

  const handleShip = async (values) => {
    try {
      await fetch(
        ShipmentResource.create(),
        { organization_id: organizationId },
        { order_id: row.order_id, product_instance_ids: selectedIds, ...values }
      );
      setShipFormOpen(false);
      await refresh();
    } catch (error) {
      handleError(error);
    }
  };

  const handleUpdate = async (shipment, body) => {
    try {
      await fetch(ShipmentResource.update(), { organization_id: organizationId, id: shipment.id }, body);
      await refresh();
    } catch (error) {
      handleError(error);
    }
  };

  const handleDelete = async (shipment) => {
    try {
      await fetch(ShipmentResource.delete(), { organization_id: organizationId, id: shipment.id });
      await refresh();
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <StyledDialog open={!!row} fullWidth maxWidth="md">
      <IconButton onClick={onClose} aria-label="close">
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Subtitle>{`Order #${row.order_id}`}</Subtitle>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '16px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <EventIcon fontSize="small" color="warning" />
            <span>{`Order placed ${formattedDate(row.order_created_at, 'datetime')}`}</span>
          </div>
          {row.shipping_address && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <LocalShippingIcon fontSize="small" color="warning" />
              <span>{formatAddress(row.shipping_address)}</span>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <EmailIcon fontSize="small" color="warning" />
            <span>{row.user_email}</span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {pendingItems.length > 0 && (
            <div
              style={{
                border: '1px solid var(--borderColor, #eee)',
                borderRadius: 8,
                padding: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <CustomChip label="Unshipped" styleKey="warning" />
                {!shipFormOpen && (
                  <Button
                    highlighted
                    smallPadded
                    disabled={selectedIds.length === 0}
                    onClick={() => setShipFormOpen(true)}
                  >
                    {`Register Shipping (${selectedIds.length})`}
                  </Button>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {pendingItems.map((item) => (
                  <label
                    key={item.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '4px 0',
                      borderTop: '1px solid var(--borderColor, #eee)',
                      cursor: 'pointer',
                    }}
                  >
                    <Checkbox
                      size="small"
                      color="warning"
                      checked={selectedIds.includes(item.id)}
                      onChange={() => toggleSelected(item.id)}
                    />
                    <span>
                      {item.title}
                      {item.variant_label && <strong> — {item.variant_label}</strong>}
                    </span>
                  </label>
                ))}
              </div>

              {shipFormOpen && (
                <ShipmentForm
                  submitLabel="Register Shipping"
                  onSubmit={handleShip}
                  onCancel={() => setShipFormOpen(false)}
                />
              )}
            </div>
          )}

          {shipments.map((shipment, index) => (
            <ShipmentCard
              key={shipment.id}
              shipment={shipment}
              number={index + 1}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
            />
          ))}
        </div>
      </DialogContent>
    </StyledDialog>
  );
};

OrderDetailsModal.propTypes = {
  row: PropTypes.object,
  organizationId: PropTypes.number,
  onChanged: PropTypes.func,
  onClose: PropTypes.func,
};

export default OrderDetailsModal;
